import React, { useEffect } from "react";
import { BasicModal } from "../../BasicModal/BasicModal";
import { ReactComponent as DebitCardIcon } from "../../../SVGcomponents/DebitCard.svg";
import styles from "./styles.module.scss";
import BasicInput from "../../../Inputs/BasicInput";
import BasicButton from "../../../Buttons/BasicButton";
import { RESPONSIVE_BREAKPOINTS } from "../../../Buttons/BasicButton";
import SimpleCountrySelect from "../../../Inputs/SimpleCountrySelect";
import axios from "../../../../api/axios";
import { useSearchParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { triggerGlobalNotifications } from "../../../../context/ErrorBridge";
import { useNotification } from "src/context/NotificationContext";
import { track, trackPurchase } from "src/config/analytics";
import { GET_USER_PAYMENTS } from "src/api/graphql/payments";
import { DepositInstanceProps } from "src/components/DepositInstance/types";

type Props = {
  closeModal: () => void;
  isOpened: boolean;
};

const LeftIcon = () => {
  return <span className={styles["left-icon"]}>$</span>;
};

// Minimum top-up in USD — drives both the validation and the error copy so the
// two can't drift apart.
const MINIMUM_PAYMENT_AMOUNT = 100;
export enum CheckoutWebhookPaymentStatusType {
  PAYMENT_APPROVED = "payment_approved",
  PAYMENT_DECLINED = "payment_declined",
  PAYMENT_CANCELED = "payment_canceled",
}

export const AddFundModal = ({ closeModal, isOpened }: Props) => {
  const [inputValue, setInputValue] = React.useState<string>("");
  const [name, setName] = React.useState<string>("");
  const [isError, setIsError] = React.useState<boolean>(false);
  const [showMinimumError, setShowMinimumError] =
    React.useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = React.useState<any>(null);
  const [searchParams, _] = useSearchParams();
  const { callNotification } = useNotification();
  const apolloClient = useApolloClient();

  useEffect(() => {
    if (window.location.href.includes("payment_status")) {
      const payment_status = searchParams.get("payment_status");
      const reference = searchParams.get("reference");
      const payload = JSON.stringify({ type: "payment_canceled", reference });

      switch (payment_status) {
        case CheckoutWebhookPaymentStatusType.PAYMENT_APPROVED: {
          // The redirect only carries the payment `reference`; the real amount
          // and whether this is the user's first top-up come from the backend
          // payments history — never from client-side storage.
          (async () => {
            let value = 0;
            let isFirstTopup = false;
            try {
              const { data } = await apolloClient.query({
                query: GET_USER_PAYMENTS,
                variables: { all: true },
                fetchPolicy: "network-only",
              });
              const payments: DepositInstanceProps[] =
                data?.getUserPayments ?? [];
              const matched = payments.find(
                (p) => p.hashId === reference || p.internalId === reference,
              );
              value = matched?.depositAmount ?? 0;
              // First top-up if this approved deposit is the only one sharing
              // its (successful) status in the user's payment history.
              const successStatus = matched?.status;
              const successfulCount = successStatus
                ? payments.filter((p) => p.status === successStatus).length
                : payments.length;
              isFirstTopup = successfulCount <= 1;
            } catch {
              // payments query failed — still report the purchase with value 0
            }

            // GA4 dedupes purchases by transaction_id, so a re-render with the
            // same reference won't double-count.
            trackPurchase({
              transaction_id: reference ?? "",
              value,
              currency: "USD",
            });

            if (isFirstTopup) {
              track("first_topup", { value, currency: "USD" });
            }
          })();

          searchParams.delete("payment_status");
          searchParams.delete("reference");
          _(searchParams);
          break;
        }
        case CheckoutWebhookPaymentStatusType.PAYMENT_CANCELED:
          (async () => {
            console.log("here");
            await axios.post("/payments/redirect/card", payload, {
              headers: {
                "X-Is-Not-Webhook": "true",
              },
            });
          })();
          searchParams.delete("payment_status");
          searchParams.delete("reference");
          _(searchParams);
          break;
      }
    }
  }, [searchParams]);

  const handleChange = (e: any) => {
    const newValue = e.currentTarget.value;

    if (newValue === "" || newValue === ".") {
      setInputValue(newValue);
      setIsError(true);
      setShowMinimumError(false);
      return;
    }

    const isValidNumber = /^\d*\.?\d{0,2}$/.test(newValue);
    if (!isValidNumber) {
      return;
    }

    setInputValue(newValue);

    const numericValue = parseFloat(newValue);
    if (isNaN(numericValue) || numericValue < MINIMUM_PAYMENT_AMOUNT) {
      setIsError(true);
      setShowMinimumError(true);
      return;
    }

    setIsError(false);
    setShowMinimumError(false);
  };

  const handleSubmit = async () => {
    try {
      const paymentData = {
        amount: inputValue,
        country: selectedCountry?.value,
        name,
      };

      console.log("Payment data:", paymentData);
      console.log("Selected country:", selectedCountry);

      const { data } = await axios.post(
        "/payments/createPayment/card",
        paymentData,
      );

      if (data.link) {
        window.open(data.link, "_blank");
        setInputValue("");
        closeModal();
      }
    } catch (error) {
      console.error("Payment error:", error);
      triggerGlobalNotifications({
        type: "error",
        data: {
          message: "Failed to create card payment. Please try again.",
          placement: "top",
        },
      });
    }
    callNotification();
  };

  const handleCloseModal = () => {
    setInputValue("");
    setSelectedCountry(null);
    setIsError(false);
    setShowMinimumError(false);
    closeModal();
  };

  return (
    <BasicModal
      sx={{ width: "482px" }}
      isOpened={isOpened}
      closeModal={handleCloseModal}
    >
      <div className={styles["title-box"]}>
        <DebitCardIcon style={{ width: "21px", height: "21px" }} />
        <h3 className={styles["title"]}>Add funds</h3>
        <span className={styles["method-type"]}>Card</span>
      </div>
      <div className={styles["form"]}>
        <div className={styles["input-box"]}>
          <label htmlFor="input">Amount</label>
          <BasicInput
            type="text"
            error={isError}
            value={inputValue}
            onChange={handleChange}
            placeholder={"$ 0.00"}
            cls={styles["modalInputWithIcon"]}
          />
          {showMinimumError && (
            <div className={styles.minimumError}>
              Minimum payment amount - ${MINIMUM_PAYMENT_AMOUNT}
            </div>
          )}
          <div className={styles["input-box"]}>
            <label htmlFor="input">Cardholder Name</label>
            <BasicInput
              type="text"
              value={name}
              onChange={(e: any) => {
                setName(e.currentTarget.value);
              }}
              placeholder={"Cardholder Name"}
              cls={styles["modalInputWithIcon"]}
            />
          </div>
        </div>
        <div className={styles["input-box"]}>
          <label htmlFor="country">Country</label>
          <div className={styles.countryPickerHolder}>
            <SimpleCountrySelect
              value={selectedCountry}
              onChange={setSelectedCountry}
              height={44}
              placeholder="Select country"
            />
          </div>
        </div>
      </div>
      <p className={styles["text"]}>
        By clicking the "Confirm" button above, you are agreeing
        <br />
        to our{" "}
        <a
          className={styles["textPrivacy"]}
          href="/terms-of-use"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service
        </a>{" "}
        and acknowledge
        <br />
        that you have read our{" "}
        <a
          className={styles["textPrivacy"]}
          href="/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
      </p>
      <div className={styles["button-box"]}>
        <BasicButton
          type="button"
          size="large"
          onClick={handleCloseModal}
          title="Cancel"
          responsiveBreakpoints={{
            mac13: {
              mediaQuery: RESPONSIVE_BREAKPOINTS.MAC_13,
              customSize: {
                height: 40,
              },
            },
          }}
          variant="secondary"
          cls={styles.cancelButton}
        />
        <BasicButton
          type="button"
          size="large"
          responsiveBreakpoints={{
            mac13: {
              mediaQuery: RESPONSIVE_BREAKPOINTS.MAC_13,
              customSize: {
                height: 40,
              },
            },
          }}
          disabled={isError || !inputValue}
          onClick={handleSubmit}
          title="Confirm"
          variant="primary"
          cls={styles.confirmButton}
        />
      </div>
    </BasicModal>
  );
};
